import { useEffect, useRef, useState, useCallback } from "react";
import { parseTimestamp } from "../utils/formatTimestamp";

// Returns null when the server didn't send a usable timestamp, so the dedupe key
// isn't built from a client-generated "now" (which would make every repeat look new).
const toUtcISOString = (value) => {
    const date = parseTimestamp(value);
    return date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
};

const getNotificationKey = (notificationData) => {
    if (notificationData.id) {
        return `id:${notificationData.id}`;
    }
    const msg = notificationData.message?.message
        || notificationData.message?.text
        || JSON.stringify(notificationData.message);
    const time = notificationData.timestamp || notificationData.message?.datetime;
    return `${msg}|${time}`;
};

export default function useNotificationSocket(userId) {
    const ws = useRef(null);
    const notificationsRef = useRef([]);
    const [notifications, setNotifications] = useState([]);
    const [latestNotification, setLatestNotification] = useState(null);
    const [status, setStatus] = useState("disconnected");

    const pingInterval = useRef(null);
    const initialSyncUntilRef = useRef(0);

    const shouldEmitLatestNotification = useCallback(() => Date.now() >= initialSyncUntilRef.current, []);

    const startPing = useCallback(() => {
        if (pingInterval.current) {
            clearInterval(pingInterval.current);
        }

        pingInterval.current = setInterval(() => {
            if (ws.current?.readyState === WebSocket.OPEN) {
                const pingPayload = {
                    event: "send_notifications",
                    user_id: parseInt(userId),
                    message: {
                        timestamp: new Date().toISOString()
                    }
                };
                ws.current.send(JSON.stringify(pingPayload));
                console.log("[Notification WS] Ping sent");
            }
        }, 50000); // 50 seconds
    }, [userId]);

    const connect = useCallback(() => {
        if (!userId) {
            console.log("[Notification WS] No userId provided, skipping connection");
            return;
        }

        // Close existing connection if any
        if (ws.current) {
            if (ws.current.readyState === WebSocket.OPEN) {
                console.log("[Notification WS] Already connected");
                return;
            }
            // Clean up existing connection
            if (ws.current.readyState === WebSocket.CONNECTING || ws.current.readyState === WebSocket.OPEN) {
                ws.current.close();
            }
        }

        // Determine WebSocket protocol based on the base URL protocol
        const baseUrl = process.env.REACT_APP_BASE_URL || "http://3.139.107.45:8001";
        const isSecure = baseUrl.startsWith("https://");
        const wsProtocol = isSecure ? "wss" : "ws";

        // Build WebSocket URL
        const cleanBase = baseUrl.replace(/^https?:\/\//, "");
        const wsUrl = `${wsProtocol}://${cleanBase.replace(/\/+$/, "")}/ws/notifications/${userId}`;

        console.log(`[Notification WS] Connecting to ${wsUrl}...`);
        ws.current = new WebSocket(wsUrl);

        ws.current.onopen = () => {
            console.log("[Notification WS] ✅ Connection established for user:", userId);
            setStatus("connected");
            initialSyncUntilRef.current = Date.now() + 3000;

            // Request any pending notifications immediately after connection
            const payload = {
                event: "send_notifications",
                user_id: parseInt(userId),
                message: {
                    timestamp: new Date().toISOString()
                }
            };
            if (ws.current.readyState === WebSocket.OPEN) {
                ws.current.send(JSON.stringify(payload));
                console.log("[Notification WS] Sent initial notification request:", payload);
                
                // Also request notifications again after a short delay to catch any missed ones
                setTimeout(() => {
                    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                        ws.current.send(JSON.stringify(payload));
                        console.log("[Notification WS] Sent follow-up notification request");
                    }
                }, 1000);
                
                startPing();
            }
        };  

        ws.current.onmessage = (event) => {
            console.log("[Notification WS] Raw message received:", event.data);
            try {
                const data = JSON.parse(event.data);
                console.log("[Notification WS] Parsed message:", JSON.stringify(data, null, 2));
                
                // Handle different message formats
                if (data.type === "pong") {
                    console.log("[Notification WS] Received pong from server");
                    return;
                }

                // Extract notification message from various possible structures
                let notificationMessage = null;
                let notificationData = null;

                // Pattern 1: data.sender === "server" && data.message exists (Backend format)
                // Backend sends: {"sender": "server", "message": {"user_id": "...", "message": "...", "datetime": "..."}}
                if (data.sender === "server" && data.message) {
                    // Extract the actual message text from data.message.message
                    notificationMessage = data.message.message || data.message.text || data.message.title || data.message.content;
                    
                    // If message is still not found, try to stringify the whole message object
                    if (!notificationMessage) {
                        notificationMessage = JSON.stringify(data.message);
                    }
                    
                    notificationData = {
                        sender: data.sender,
                        message: data.message,
                        timestamp: toUtcISOString(data.message.datetime || data.message.timestamp || data.timestamp),
                        user_id: data.message.user_id || null,
                        id: data.message.id || data.message.notification_id || data.id || null,
                        read: false
                    };
                    
                    console.log("[Notification WS] Pattern 1 matched - Backend format detected");
                    console.log("[Notification WS] Extracted message:", notificationMessage);
                    console.log("[Notification WS] Full message object:", data.message);
                }
                // Pattern 2: data.message is a string
                else if (data.message && typeof data.message === 'string') {
                    notificationMessage = data.message;
                    notificationData = {
                        sender: data.sender || "server",
                        message: { message: data.message },
                        timestamp: toUtcISOString(data.timestamp),
                        read: false
                    };
                }
                // Pattern 3: data has notification properties directly
                else if (data.notification || data.text || data.title || data.content) {
                    notificationMessage = data.notification || data.text || data.title || data.content;
                    notificationData = {
                        sender: data.sender || "server",
                        message: { message: notificationMessage },
                        timestamp: toUtcISOString(data.timestamp || data.created_at),
                        read: false
                    };
                }
                // Pattern 4: explicit notification events only (not send_notifications ack/replay)
                else if (data.event === "notification") {
                    notificationMessage = data.data?.message || data.data?.text || data.data?.notification || JSON.stringify(data.data || data);
                    notificationData = {
                        sender: data.sender || "server",
                        message: data.data || { message: notificationMessage },
                        timestamp: toUtcISOString(data.timestamp || data.data?.timestamp),
                        read: false
                    };
                }
                // Pattern 5: Try to find any meaningful message in the data structure
                else if (data.data && (data.data.message || data.data.text || data.data.notification)) {
                    notificationMessage = data.data.message || data.data.text || data.data.notification;
                    notificationData = {
                        sender: data.sender || "server",
                        message: data.data,
                        timestamp: toUtcISOString(data.timestamp || data.data.timestamp),
                        read: false
                    };
                }

                // If we found a notification, add it
                if (notificationMessage && notificationData) {
                    // Key from server-provided fields only, then fill the display timestamp.
                    const notificationKey = getNotificationKey(notificationData);
                    notificationData._key = notificationKey;
                    notificationData.timestamp = notificationData.timestamp || new Date().toISOString();

                    const isDuplicate = notificationsRef.current.some(
                        (n) => n._key === notificationKey
                    );

                    if (isDuplicate) {
                        console.log("[Notification WS] Duplicate notification ignored:", notificationKey);
                    } else {
                        const nextNotifications = [...notificationsRef.current, notificationData].slice(-50);
                        notificationsRef.current = nextNotifications;
                        setNotifications(nextNotifications);
                        console.log("[Notification WS] Adding new notification:", notificationMessage);

                        if (shouldEmitLatestNotification()) {
                            // Use full notification object so repeated message text with different
                            // timestamps still triggers downstream effects (e.g. API refresh).
                            setLatestNotification({ ...notificationData });
                            console.log("[Notification WS] ✅ New notification added successfully:", notificationMessage);
                        } else {
                            console.log("[Notification WS] Initial sync replay ignored for badge update:", notificationMessage);
                        }
                    }
                } else {
                    console.log("[Notification WS] ⚠️ Ignored message - no notification found:", JSON.stringify(data, null, 2));
                }

            } catch (err) {
                console.warn("[Notification WS] ❌ Failed to parse message:", event.data, err);
            }
        };

        ws.current.onerror = (error) => {
            console.error("[Notification WS] Error:", error);
            setStatus("error");
        };

        ws.current.onclose = (event) => {
            console.warn("[Notification WS] Connection closed", event.code, event.reason);
            setStatus("disconnected");
            
            // Clear ping interval
            if (pingInterval.current) {
                clearInterval(pingInterval.current);
                pingInterval.current = null;
            }
            
            // Only reconnect if it wasn't a manual close (code 1000)
            if (event.code !== 1000 && userId) {
                setTimeout(() => {
                    console.log("[Notification WS] Attempting to reconnect...");
                    connect();
                }, 3000);
            }
        };
    }, [userId, startPing, shouldEmitLatestNotification]);

    const disconnect = useCallback(() => {
        console.log("[Notification WS] Disconnecting...");
        
        // Clear ping interval
        if (pingInterval.current) {
            clearInterval(pingInterval.current);
            pingInterval.current = null;
        }
        
        // Close WebSocket connection
        if (ws.current) {
            ws.current.close(1000, "Manual disconnect");
            ws.current = null;
        }
        
        setStatus("disconnected");
        notificationsRef.current = [];
        setNotifications([]);
        setLatestNotification(null);
    }, []);

    const sendNotificationEvent = useCallback(() => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            const payload = {
                event: "send_notifications",
                user_id: parseInt(userId),
                message: {
                    timestamp: new Date().toISOString()
                }
            };
            ws.current.send(JSON.stringify(payload));
            console.log("[Notification WS] Sent notification event:", payload);
        } else {
            console.warn("[Notification WS] Cannot send notification event - socket not open");
        }
    }, [userId]);

    const markAsRead = useCallback((index) => {
        setNotifications(prev => {
            const updated = [...prev];
            if (updated[index]) {
                updated[index] = { ...updated[index], read: true };
            }
            notificationsRef.current = updated;
            return updated;
        });
    }, []);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => {
            const updated = prev.map(n => ({ ...n, read: true }));
            notificationsRef.current = updated;
            return updated;
        });
    }, []);

    const removeNotification = useCallback((index) => {
        setNotifications(prev => {
            const updated = prev.filter((_, i) => i !== index);
            notificationsRef.current = updated;
            return updated;
        });
    }, []);

    useEffect(() => {
        if (userId) {
            connect();
        }
        
        return () => {
            // Clear ping interval
            if (pingInterval.current) {
                clearInterval(pingInterval.current);
                pingInterval.current = null;
            }
            
            // Close WebSocket connection properly
            if (ws.current) {
                if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                    ws.current.close(1000, "Component unmounting");
                }
                ws.current = null;
            }
        };
    }, [connect, userId]);

    // Helper function to check connection status
    const isConnected = () => {
        return ws.current && ws.current.readyState === WebSocket.OPEN;
    };

    return {
        status,
        notifications,
        latestNotification,
        connect,
        disconnect,
        sendNotificationEvent,
        isConnected,
        markAsRead,
        markAllAsRead,
        removeNotification
    };
}